import { useEffect, useId, useRef, useState } from 'react';
import type { GeocodePlace } from '@repo/shared';
import { useGeocodeSearch } from '../features/geocode/hooks';
import styles from './SearchBox.module.css';

export interface SearchBoxProps {
  /** False when no Mapbox token is set — the input explains itself instead. */
  enabled: boolean;
  /** `lon,lat` of the map centre, used to bias results toward what's on screen. */
  proximity?: string;
  onSelect: (place: GeocodePlace) => void;
}

/**
 * Address search with a keyboard-navigable results list.
 *
 * Implemented as an ARIA combobox rather than a plain input so the results are
 * reachable without a mouse and announced by screen readers.
 */
export function SearchBox({ enabled, proximity, onSelect }: SearchBoxProps) {
  const [term, setTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const search = useGeocodeSearch(term, { proximity, enabled });
  const places = search.data?.places ?? [];

  // Collapse the results when focus or a click lands outside the combobox.
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  // A new result set invalidates whatever row was highlighted.
  useEffect(() => setHighlighted(0), [search.data]);

  function choose(place: GeocodePlace) {
    onSelect(place);
    setTerm(place.name);
    setIsOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (places.length === 0) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      setHighlighted((current) => {
        const next = current + (event.key === 'ArrowDown' ? 1 : -1);
        return (next + places.length) % places.length;
      });
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const place = places[highlighted];
      if (place) choose(place);
    }
  }

  const showList = isOpen && search.isActive;

  return (
    <div className={styles.search} ref={containerRef}>
      <div className={styles.inputWrap}>
        <SearchIcon />
        <input
          type="search"
          className={styles.input}
          value={term}
          disabled={!enabled}
          placeholder={enabled ? 'Search an address or place…' : 'Search needs a Mapbox token'}
          onChange={(event) => {
            setTerm(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            showList && places[highlighted] ? `${listId}-${highlighted}` : undefined
          }
        />
        {search.isSearching && <span className={styles.spinner} aria-hidden="true" />}
      </div>

      {showList && (
        <ul className={styles.results} id={listId} role="listbox" aria-label="Search results">
          {search.isError && <li className={styles.message}>Search is unavailable right now.</li>}

          {!search.isError && !search.isSearching && places.length === 0 && (
            <li className={styles.message}>No places matched “{term.trim()}”.</li>
          )}

          {places.map((place, index) => (
            <li key={place.id} role="none">
              <button
                type="button"
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === highlighted}
                className={`${styles.result} ${index === highlighted ? styles.highlighted : ''}`}
                onPointerEnter={() => setHighlighted(index)}
                onClick={() => choose(place)}
              >
                <span className={styles.resultName}>{place.name}</span>
                {place.context && <span className={styles.resultContext}>{place.context}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      className={styles.icon}
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5 14 14" strokeLinecap="round" />
    </svg>
  );
}
